import { useEffect, useState, useCallback } from 'react'
import { getGuests } from '@unvld/db'
import type { Guest } from '@unvld/db'
import { Users, Search, Filter, Download } from 'lucide-react'
import { GuestDetailModal } from '../components/GuestDetailModal'

const TYPE_LABELS: Record<string, string> = {
  couple: 'Paar',
  single_male: 'Einzelherr',
  single_female: 'Einzeldame',
  group: 'Gruppe',
  unknown: 'Unbekannt',
}

const TAG_STYLES: Record<string, string> = {
  VIP: 'bg-purple-500/20 text-purple-400',
  blacklisted: 'bg-red-500/20 text-red-400',
  regular: 'bg-blue-500/20 text-blue-400',
  newcomer: 'bg-green-500/20 text-green-400',
}

const FILTER_TAGS = ['VIP', 'regular', 'newcomer', 'blacklisted'] as const

function getTagStyle(tag: string) {
  return TAG_STYLES[tag] || 'bg-white/10 text-white/70'
}

function exportGuestsCSV(guests: Guest[]) {
  const headers = ['Pseudonym', 'Typ', 'Besuche', 'Umsatz (EUR)', 'Tags', 'Letzter Besuch']
  const rows = guests.map(g => [
    g.pseudonym,
    TYPE_LABELS[g.guest_type] || g.guest_type,
    String(g.visit_count),
    (g.total_spend_cents / 100).toFixed(2).replace('.', ','),
    (g.tags || []).join('; '),
    g.last_visit ? new Date(g.last_visit).toLocaleDateString('de-DE') : '',
  ])

  const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(';')).join('\n')
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `gaeste_${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

export function GuestsPage() {
  const [guests, setGuests] = useState<Guest[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [activeTagFilter, setActiveTagFilter] = useState<string | null>(null)
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  const fetchGuests = useCallback(async (query?: string, tagFilter?: string | null) => {
    setLoading(true)
    try {
      const result = await getGuests({
        search: query || undefined,
        tags: tagFilter ? [tagFilter] : undefined,
        limit: 50,
      })
      setGuests(result.guests)
      setTotal(result.total)
    } catch (err) {
      console.error(err)
    }
    setLoading(false)
  }, [])

  useEffect(() => { fetchGuests(search, activeTagFilter) }, [activeTagFilter])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    fetchGuests(search, activeTagFilter)
  }

  const toggleTagFilter = (tag: string) => {
    setActiveTagFilter((prev) => (prev === tag ? null : tag))
  }

  const openGuestDetail = (guest: Guest) => {
    setSelectedGuest(guest)
    setModalOpen(true)
  }

  const handleTagsUpdated = (guestId: string, newTags: string[]) => {
    setGuests((prev) =>
      prev.map((g) => (g.id === guestId ? { ...g, tags: newTags } : g))
    )
    if (selectedGuest?.id === guestId) {
      setSelectedGuest((prev) => prev ? { ...prev, tags: newTags } : null)
    }
  }

  const handleModalClose = () => {
    setModalOpen(false)
    // Re-fetch if a tag filter is active, in case tags changed
    if (activeTagFilter) {
      fetchGuests(search, activeTagFilter)
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Gäste</h1>
          <p className="text-slate-400 text-sm mt-1">{total} Gäste gesamt</p>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Pseudonym suchen..."
            className="w-full pl-10 pr-4 py-2.5 bg-slate-900 border border-slate-800 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </form>

      {/* Tag Filters */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <Filter className="w-4 h-4 text-slate-500" />
        <span className="text-xs text-slate-500 mr-1">Filter:</span>
        {FILTER_TAGS.map((tag) => (
          <button
            key={tag}
            onClick={() => toggleTagFilter(tag)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              activeTagFilter === tag
                ? getTagStyle(tag) + ' ring-1 ring-white/20'
                : 'bg-white/5 text-slate-400 hover:bg-white/10'
            }`}
          >
            {tag}
          </button>
        ))}
        {activeTagFilter && (
          <button
            onClick={() => setActiveTagFilter(null)}
            className="text-xs text-slate-500 hover:text-white transition-colors ml-1"
          >
            Zurücksetzen
          </button>
        )}
        <div className="ml-auto">
          <button
            onClick={() => exportGuestsCSV(guests)}
            disabled={guests.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/15 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            CSV Export
          </button>
        </div>
      </div>

      {/* Guest List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : guests.length === 0 ? (
        <div className="text-center py-20 bg-slate-900 border border-slate-800 rounded-xl">
          <Users className="w-16 h-16 text-slate-700 mx-auto mb-4" />
          <h2 className="text-xl text-white font-semibold mb-2">
            {activeTagFilter ? 'Keine Gäste mit diesem Tag' : 'Noch keine Gäste'}
          </h2>
          <p className="text-slate-400">
            {activeTagFilter
              ? 'Versuche einen anderen Filter oder setze den Filter zurück.'
              : 'Gäste werden automatisch bei der ersten Buchung angelegt.'}
          </p>
        </div>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-auto max-h-[calc(100vh-16rem)]">
          <table className="w-full">
            <thead className="sticky top-0 z-10 bg-slate-900">
              <tr className="border-b border-slate-800">
                <th scope="col" className="text-left px-4 py-3 text-xs text-slate-400">Pseudonym</th>
                <th scope="col" className="text-left px-4 py-3 text-xs text-slate-400">Typ</th>
                <th scope="col" className="text-left px-4 py-3 text-xs text-slate-400">Besuche</th>
                <th scope="col" className="text-left px-4 py-3 text-xs text-slate-400">Ausgaben</th>
                <th scope="col" className="text-left px-4 py-3 text-xs text-slate-400">Tags</th>
                <th scope="col" className="text-left px-4 py-3 text-xs text-slate-400">Letzter Besuch</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {guests.map((g) => (
                <tr
                  key={g.id}
                  onClick={() => openGuestDetail(g)}
                  className="hover:bg-slate-800/30 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 text-white font-medium">{g.pseudonym}</td>
                  <td className="px-4 py-3 text-sm text-slate-300">{TYPE_LABELS[g.guest_type] || g.guest_type}</td>
                  <td className="px-4 py-3 text-sm text-slate-300">{g.visit_count}</td>
                  <td className="px-4 py-3 text-sm text-slate-300">{(g.total_spend_cents / 100).toFixed(0)} &euro;</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {g.tags?.map((tag) => (
                        <span
                          key={tag}
                          className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${getTagStyle(tag)}`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {g.last_visit ? new Date(g.last_visit).toLocaleDateString('de-DE') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Guest Detail Modal */}
      <GuestDetailModal
        guest={selectedGuest}
        open={modalOpen}
        onClose={handleModalClose}
        onTagsUpdated={handleTagsUpdated}
      />
    </div>
  )
}
